import React from 'react';
import { Card } from '@vehix/portal/components/ui/card';
import { cn } from '@vehix/portal/lib/utils';
import type { LucideIcon } from 'lucide-react';

/**
 * Visual emphasis level for a stat card.
 * - undefined: neutral
 * - 'warning': amber accent — something might need attention
 * - 'critical': red accent — something is broken / requires action
 */
export type AttentionLevel = 'critical' | 'warning' | undefined;

export interface StatCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  iconColor?: string;
  attention?: AttentionLevel;
  loading?: boolean;
  /** When provided, the entire card becomes a click target (e.g., to deep-link into a related admin page). */
  onClick?: () => void;
  /** Optional small caption rendered below the value. */
  description?: string;
}

const ATTENTION_STYLES: Record<NonNullable<AttentionLevel>, { cardClass: string; iconColor: string }> = {
  critical: {
    cardClass: 'border-red-500/50 bg-red-50 dark:bg-red-950/20',
    iconColor: 'text-red-500'
  },
  warning: {
    cardClass: 'border-amber-500/50 bg-amber-50 dark:bg-amber-950/20',
    iconColor: 'text-amber-500'
  }
};

const formatValue = (value: string | number): string =>
  typeof value === 'number' ? value.toLocaleString() : value;

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon: Icon,
  iconColor = 'text-primary',
  attention,
  loading = false,
  onClick,
  description
}) => {
  const styles = attention ? ATTENTION_STYLES[attention] : null;
  const finalIconColor = styles?.iconColor || iconColor;
  const cardClass = styles?.cardClass || '';

  const content = loading ? (
    <div className='flex items-center gap-3'>
      <div className='h-10 w-10 bg-muted animate-pulse rounded' />
      <div className='flex-1'>
        <div className='h-3 bg-muted animate-pulse rounded w-20 mb-2' />
        <div className='h-6 bg-muted animate-pulse rounded w-16' />
      </div>
    </div>
  ) : (
    <div className='flex items-center gap-3'>
      <Icon className={cn('h-6 w-6 flex-shrink-0', finalIconColor)} />
      <div className='min-w-0 flex-1'>
        <p className='text-xs text-muted-foreground truncate'>{title}</p>
        <p className='text-2xl font-bold truncate leading-tight'>{formatValue(value)}</p>
        {description && (
          <p className='text-[11px] text-muted-foreground truncate mt-0.5'>{description}</p>
        )}
      </div>
    </div>
  );

  if (onClick) {
    return (
      <Card
        className={cn(
          'p-4 cursor-pointer transition-all hover:shadow-md hover:border-primary/40',
          cardClass
        )}
      >
        <button
          type='button'
          onClick={onClick}
          className='w-full text-left focus:outline-none focus:ring-2 focus:ring-primary rounded'
        >
          {content}
        </button>
      </Card>
    );
  }

  return <Card className={cn('p-4', cardClass)}>{content}</Card>;
};

export default StatCard;
